import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { Browser } from '@capacitor/browser';
import { Platform } from '@ionic/angular';
import { MaskitoElementPredicate, MaskitoOptions, maskitoTransform } from '@maskito/core';
import { select, Store } from '@ngrx/store';

import { compact, find, isNil, isUndefined, snakeCase } from 'lodash-es';
import { DateTime } from 'luxon';
import { map, Observable, of, tap } from 'rxjs';
import { concatMap, filter } from 'rxjs/operators';

import { amountMask, cleanUpMaskValue, phoneMask, surfaceMask } from '../../../../../constants/maskito.constants';
import { AddressEnum } from '../../../../../enums/address';
import { MainTabBasePathEnum } from '../../../../../enums/main-tabs';
import { ProfileSearchEnum } from '../../../../../enums/profile-search';
import * as fromPropertyProfileActions from '../../../../../store/actions/property-profile.actions';
import * as fromWalkingFarmActions from '../../../../../store/actions/walking-farm.actions';
import * as fromOrganizationSelectors from '../../../../../store/selectors/organization.selectors';
import * as fromWalkingFarmSelectors from '../../../../../store/selectors/walking-farm.selectors';
import { WalkingFarmProperty } from '../../../../../typings/walking-farm';
import { AddressPipe } from '../../../../pipes/address.pipe';
import { DashIfEmptyPipe } from '../../../../pipes/dash-if-empty.pipe';
import { SurfacePipe } from '../../../../pipes/surface.pipe';
import { UsdCurrencyPipe } from '../../../../pipes/usd-currency.pipe';
import { EmailComposerService } from '../../../../services/email-composer.service';
import { FileService } from '../../../../services/file.service';

interface PropertyFormGroup {
  bathrooms: FormControl<number | null>;
  bedrooms: FormControl<number | null>;
  email: FormControl<string | null>;
  lotSize: FormControl<string | null>;
  notes: FormControl<string | null>;
  phone: FormControl<string | null>;
  pool: FormControl<string | null>;
  primaryOwnerName: FormControl<string | null>;
  propertySize: FormControl<string | null>;
  secondaryOwnerName: FormControl<string | null>;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-walking-farm-view-property',
  standalone: false,
  templateUrl: './home.component.html'
})
export class HomePageComponent {
  private activatedRoute = inject(ActivatedRoute);
  private addressPipe = inject(AddressPipe);
  private dashIfEmptyPipe = inject(DashIfEmptyPipe);
  private datePipe = inject(DatePipe);
  private emailComposerService = inject(EmailComposerService);
  private fileService = inject(FileService);
  private platform = inject(Platform);
  private store = inject(Store);
  private surfacePipe = inject(SurfacePipe);
  private usdCurrencyPipe = inject(UsdCurrencyPipe);

  walkingFarmId: string | null;
  propertyId: string | null;
  private appName = 'SmartDirect&#174; Mobile';

  property$: Observable<WalkingFarmProperty>;

  property?: WalkingFarmProperty;

  selectFarmAlertsEnabled$: Observable<boolean>;

  propertyForm: FormGroup<PropertyFormGroup> = new FormGroup<PropertyFormGroup>({
    bathrooms: new FormControl<number>(0),
    bedrooms: new FormControl<number>(0),
    email: new FormControl<string>('', [Validators.email, Validators.maxLength(254)]),
    lotSize: new FormControl<string>('0'),
    notes: new FormControl<string>(''),
    phone: new FormControl<string>(''),
    pool: new FormControl<string>('N'),
    primaryOwnerName: new FormControl<string>(''),
    propertySize: new FormControl<string>('0'),
    secondaryOwnerName: new FormControl<string>('')
  });

  defaultBackHref: string | undefined;
  defaultHref: string;

  constructor() {
    this.walkingFarmId = this.activatedRoute.snapshot.paramMap.get('id');
    this.propertyId = this.activatedRoute.snapshot.paramMap.get('propertyId');

    this.defaultBackHref = this.activatedRoute.snapshot.data['defaultBackHref'];
    this.defaultHref = [this.defaultBackHref ?? '', 'walking-farm', this.walkingFarmId, 'view'].join('/');

    this.store
      .pipe(
        select(fromOrganizationSelectors.selectApplicationName),
        filter((appName) => !isNil(appName))
      )
      .subscribe((appName) => {
        this.appName = appName;
      });

    this.property$ = of(this.walkingFarmId).pipe(
      filter((id) => !isNil(id)),
      concatMap((id) => this.store.pipe(select(fromWalkingFarmSelectors.selectWalkingFarm(id)))),
      filter((walkingFarm) => !isUndefined(walkingFarm)),
      map((walkingFarm) =>
        find(walkingFarm.properties, (property) => `${property.A000_PropertyNumber}` === this.propertyId)
      ),
      filter((walkingFarmProperty) => !isUndefined(walkingFarmProperty)),
      tap((property) => {
        this.property = property;
        let lotSize = parseFloat(`${property.A035_LotArea}`);
        if (isNaN(lotSize)) {
          lotSize = 0;
        }
        let propertySize = parseFloat(`${property.A022_BuildingArea}`);
        if (isNaN(propertySize)) {
          propertySize = 0;
        }
        let bedrooms = parseFloat(`${property.A024_Bedrooms}`);
        if (isNaN(bedrooms)) {
          bedrooms = 0;
        }
        let bathrooms = parseFloat(`${property.A025_Bathrooms}`);
        if (isNaN(bathrooms)) {
          bathrooms = 0;
        }

        this.propertyForm.patchValue({
          bathrooms,
          bedrooms,
          email: property.A045_OwnerEmail,
          lotSize: maskitoTransform(`${lotSize}`, this.surfaceMask),
          notes: property.Notes,
          phone: maskitoTransform(property.A044_OwnerPhone, this.phoneMask),
          pool: property.A026_Pool === 'P' ? 'P' : 'N',
          primaryOwnerName: property.A016_OwnerName,
          propertySize: maskitoTransform(`${propertySize}`, this.surfaceMask),
          secondaryOwnerName: property.A009_OwnerName2
        });
      })
    );

    this.selectFarmAlertsEnabled$ = this.store.pipe(select(fromOrganizationSelectors.selectFarmAlertsEnabled));
  }

  async getDirections() {
    await this.platform.ready();
    const isIOS = this.platform.is('ios');
    const address = encodeURIComponent(
      compact([
        this.property?.A002_HouseNumber,
        this.property?.A004_StreetName,
        this.property?.A046_City,
        this.property?.A047_State,
        this.property?.A007_ZipCode,
        'US'
      ]).join(', ')
    );
    await Browser.open({
      url: isIOS
        ? `http://maps.apple.com/?daddr=${address}`
        : `https://www.google.com/maps/dir/?api=1&destination=${address}`,
      windowName: '_blank'
    });
  }

  async exportLead() {
    if (this.property) {
      const data = [
        [
          `Lead Status: ${this.property.Status}`,
          'Property Address',
          this.dashIfEmptyPipe.transform(
            compact([
              this.addressPipe.transform(this.property, AddressEnum.LineOne),
              this.addressPipe.transform(this.property, AddressEnum.LineTwo)
            ]).join('\n')
          )
        ].join('\n'),
        [
          'Owner Information',
          `${this.property.A009_OwnerName2}`,
          `Phone: ${this.dashIfEmptyPipe.transform(this.property.A044_OwnerPhone)}`,
          `Email: ${this.dashIfEmptyPipe.transform(this.property.A045_OwnerEmail)}`
        ].join('\n'),
        ['Notes:', `${this.dashIfEmptyPipe.transform(this.property.Notes)}`].join('\n'),
        [
          `Beds: ${this.dashIfEmptyPipe.transform(this.property.A024_Bedrooms)}`,
          `Baths: ${this.dashIfEmptyPipe.transform(this.property.A025_Bathrooms)}`,
          `Pool: ${this.property.A026_Pool === 'P' ? 'Yes' : 'No'}`,
          `Sqft: ${this.surfacePipe.transform(this.property.A022_BuildingArea)}`,
          `Lot: ${this.surfacePipe.transform(this.property.A035_LotArea)}`,
          `Built: ${this.dashIfEmptyPipe.transform(this.property.A038_YearBuilt)}`,
          `Use: ${this.dashIfEmptyPipe.transform(this.property.A020_UseCodeDescription)}`
        ].join('\n'),
        [
          `Sale Price: ${this.usdCurrencyPipe.transform(this.property.A029_SalesPrice)}`,
          `Sale Date: ${this.datePipe.transform(this.property.A030_SaleDate, 'MM/dd/yyyy')}`,
          `Doc #: ${this.dashIfEmptyPipe.transform(this.property.A031_SalesDocumentNumber)}`,
          `Assessed: ${this.usdCurrencyPipe.transform(this.property.A032_AssessedLandValue)}`,
          `Impr: ${this.usdCurrencyPipe.transform(this.property.A034_AssessedImprovementValue)}`,
          `Zn: ${this.dashIfEmptyPipe.transform(this.property.A021_Zoning)}`
        ].join('\n'),
        [
          `Sent from ${this.appName.includes('PropertyForce') ? 'SmartDirect' : this.appName} by Red Shed Technology.`
        ].join('\n')
      ].join('\n--------\n');

      await this.fileService.exportDocument(
        new Blob([data], {
          type: 'text/html'
        }),
        `${snakeCase([this.addressPipe.transform(this.property, AddressEnum.Full), 'Lead', DateTime.now().toFormat('X')].join(' '))}.txt`,
        'Lead Export',
        'Export Lead'
      );
    }
  }

  loadProfile() {
    this.store.dispatch(
      fromPropertyProfileActions.loadProfileByAddress({
        payload: {
          address: this.addressPipe.transform(this.property, AddressEnum.LineOne),
          searchType:
            this.defaultBackHref === MainTabBasePathEnum.PropertySearch
              ? ProfileSearchEnum.PropertySearchWalkingFarm
              : ProfileSearchEnum.WalkingFarm,
          zip: this.addressPipe.transform(this.property, AddressEnum.ZipFull)
        }
      })
    );
  }

  ionViewWillLeave() {
    if (this.walkingFarmId && this.property && this.propertyForm.dirty) {
      const updatedData = this.propertyForm.getRawValue();
      const updatedProperty = {
        ...this.property,
        A009_OwnerName2: updatedData.secondaryOwnerName ?? '',
        A016_OwnerName: updatedData.primaryOwnerName ?? '',
        A022_BuildingArea: cleanUpMaskValue(updatedData.propertySize),
        A024_Bedrooms: updatedData.bedrooms ?? 0,
        A025_Bathrooms: updatedData.bathrooms ?? 0,
        A026_Pool: updatedData.pool ?? 'N',
        A035_LotArea: cleanUpMaskValue(updatedData.lotSize),
        A044_OwnerPhone: (updatedData.phone ?? '').replace(/\D/g, ''),
        A045_OwnerEmail: updatedData.email ?? '',
        Notes: updatedData.notes ?? ''
      };

      this.store.dispatch(
        fromWalkingFarmActions.updateProperty({
          payload: {
            farmId: this.walkingFarmId,
            property: updatedProperty
          }
        })
      );
    }
  }

  async composeEmail(emailAddress: string) {
    await this.emailComposerService.composeEmail({ to: [emailAddress] });
  }

  readonly surfaceMask: MaskitoOptions = surfaceMask;
  readonly phoneMask: MaskitoOptions = phoneMask;
  readonly maskPredicate: MaskitoElementPredicate = async (el) =>
    (el as unknown as HTMLIonInputElement).getInputElement();
  protected readonly addressType = AddressEnum;
  protected readonly amountMask = amountMask;
}
